import { useEffect, useRef, useState } from "react";
import "@/App.css";

import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";

import Landing from "@/pages/Landing";
import ComingSoon from "@/pages/ComingSoon";
import SongCatalogue from "@/pages/SongCatalogue";
import CheckoutPage from "@/pages/CheckoutPage";
import Members from "@/pages/Members";
import Dashboard from "@/pages/Dashboard";
import StreamPresets from "@/pages/StreamPresets";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";

import { MusicProvider } from "@/context/MusicContext";
import { AuthProvider } from "@/context/AuthContext";
import MemberAccountMenu from "@/components/MemberAccountMenu";
import { Nav } from "@/components/Nav";
import { BackgroundMusic } from "@/components/BackgroundMusic";
import { AudioConsent } from "@/components/AudioConsent";
import ComikReleaf from "@/pages/ComikReleaf";

const BACKEND_URL = (
    process.env.REACT_APP_BACKEND_URL ||
    "https://https-doriantelive-api-onrender-com.onrender.com"
).replace(/\/+$/, "");

const normalizeCartImage = (image) => {
    if (typeof image !== "string" || !image) {
        return image;
    }

    if (/^https?:\/\//i.test(image)) {
        return image;
    }

    return `${BACKEND_URL}${image.startsWith("/") ? "" : "/"}${image}`;
};

function GlobalAudio({ enabled }) {
  return (
    <BackgroundMusic
      enabled={enabled}
    />
  );
}

function App() {

  // Global audio state
  const [audioEnabled, setAudioEnabled] = useState(false);

  // Global merch cart state
  const [cart, setCart] = useState(() => {
      try {
          const savedCart = localStorage.getItem("doriantelive_cart");

          if (!savedCart) {
              return [];
          }

          const parsedCart = JSON.parse(savedCart);

          if (!Array.isArray(parsedCart)) {
              return [];
          }

          return parsedCart.map((item) => ({
              ...item,
              image: normalizeCartImage(item.image),
          }));
      } catch (error) {
          console.error("Failed to load saved cart:", error);
          return [];
      }
  });

  const transferToken = new URLSearchParams(
    window.location.search
  ).get("transfer");

  const [cartTransferStatus, setCartTransferStatus] = useState(
    () => (transferToken ? "loading" : "idle")
  );

  const [cartTransferError, setCartTransferError] = useState("");

  const processedTransferToken = useRef(null);

  useEffect(() => {
    if (!transferToken) {
      setCartTransferStatus("idle");
      return;
    }

    if (processedTransferToken.current === transferToken) {
      return;
    }

    processedTransferToken.current = transferToken;

    const consumeCartTransfer = async () => {
      setCartTransferStatus("loading");
      setCartTransferError("");

      const backendUrl = (
        process.env.REACT_APP_BACKEND_URL || ""
      ).replace(/\/$/, "");

      if (!backendUrl) {
        setCartTransferError("The checkout server is not configured.");
        setCartTransferStatus("error");
        return;
      }

      try {
        const response = await fetch(
          `${backendUrl}/api/cart-transfer/consume`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              token: transferToken,
            }),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.detail || "Your cart transfer could not be completed."
          );
        }

        if (!Array.isArray(data.cart)) {
          throw new Error("The server returned an invalid cart.");
        }

        const restoredCart = data.cart.map((item) => ({
            ...item,
            id: `${item.productId || item.product_id}-${item.variantId || item.variant_id}`,
            productId: item.productId || item.product_id,
            variantId: item.variantId || item.variant_id,
            image: normalizeCartImage(item.image),
            quantity: Number(item.quantity || 1),
        }));

        localStorage.setItem(
          "doriantelive_cart",
          JSON.stringify(restoredCart)
        );

        if (data.region === "CA" || data.region === "US") {
          localStorage.setItem(
            "comikReleafRegion",
            data.region
          );
        }

        setCart(restoredCart);

        const url = new URL(window.location.href);
        url.searchParams.delete("transfer");

        window.history.replaceState(
          {},
          "",
          `${url.pathname}${url.search}${url.hash}`
        );

        setCartTransferStatus("success");
      } catch (error) {
        console.error("Cart transfer consumption failed:", error);
        setCartTransferError(
          error.message || "Your cart could not be restored."
        );
        setCartTransferStatus("error");
      }
    };

    consumeCartTransfer();
  }, [transferToken, setCart]);  

  useEffect(() => {
      localStorage.setItem(
          "doriantelive_cart",
          JSON.stringify(cart)
      );
  }, [cart]);

  const removeFromCart = (itemId) => {
      setCart((currentCart) =>
          currentCart.filter(
              (item) => item.id !== itemId
          )
      );
  };

  const quantityCheckPending = useRef(false);

  const updateCartQuantity = async (itemId, quantity) => {
    const item = cart.find((entry) => entry.id === itemId);
    if (!item) return;

    const requestedQuantity = Math.max(
      1,
      Math.floor(Number(quantity) || 1)
    );

    // Decreasing quantity never requires an inventory request.
    if (requestedQuantity <= Number(item.quantity)) {
      setCart((current) =>
        current.map((entry) =>
          entry.id === itemId
            ? { ...entry, quantity: requestedQuantity }
            : entry
        )
      );
      return;
    }

    const region = localStorage.getItem("comikReleafRegion");

    // Preserve the existing US fulfillment behavior.
    if (region !== "CA") {
      setCart((current) =>
        current.map((entry) =>
          entry.id === itemId
            ? { ...entry, quantity: requestedQuantity }
            : entry
        )
      );
      return;
    }

    // Prevent simultaneous stock requests from repeated clicks.
    if (quantityCheckPending.current) return;
    quantityCheckPending.current = true;

    try {
      const params = new URLSearchParams({
        product_id: item.productId,
        variant_id: item.variantId,
      });

      const response = await fetch(
        `${BACKEND_URL}/api/merch/stock?${params}`
      );

      if (!response.ok) {
        throw new Error("Unable to verify inventory.");
      }

      const inventory = await response.json();

      if (!inventory.available) {
        window.alert(
          "This size and color is currently sold out."
        );
        return;
      }

      setCart((current) => {
      const matchingQuantity = current.reduce(
          (total, entry) => {
              const sameGarment =
                  item.inventory_sku && entry.inventory_sku
                      ? entry.inventory_sku === item.inventory_sku
                      : entry.variantId === item.variantId;

              return sameGarment
                  ? total + Number(entry.quantity || 1)
                  : total;
          },
          0
      );

        const existingQuantity = Number(
          current.find((entry) => entry.id === itemId)?.quantity || 0
        );

        const totalRequested =
          matchingQuantity - existingQuantity + requestedQuantity;

        if (
          !inventory.abundant &&
          totalRequested > inventory.stock
        ) {
          window.alert(
            `Only ${Math.max(0, inventory.stock)} available in this size and color.`
          );
          return current;
        }

        return current.map((entry) =>
          entry.id === itemId
            ? { ...entry, quantity: requestedQuantity }
            : entry
        );
      });
    } catch (error) {
      console.error("Cart inventory verification failed:", error);

      window.alert(
        "We couldn't check availability right now. Please try again."
      );
    } finally {
      quantityCheckPending.current = false;
    }
  };

  // Always show consent when the site loads
  const [showAudioConsent, setShowAudioConsent] = useState(true);

  const isComikReleafDomain =
    window.location.hostname === "comikreleaf.com" ||
    window.location.hostname === "comikreleaf.ca";

  useEffect(() => {

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    let raf;

    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);

    // Smooth scrolling for anchor links
    const onClick = (e) => {

      const a = e.target.closest('a[href^="#"]');

      if (!a) return;

      const id = a.getAttribute("href");

      if (id.length > 1) {

        const el = document.querySelector(id);

        if (el) {

          e.preventDefault();

          lenis.scrollTo(el, {
            offset: -60,
          });

        }
      }
    };

    document.addEventListener("click", onClick);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };

  }, []);

  return (
    <div className="App">

      {showAudioConsent && (
        <AudioConsent
          onAllow={() => {
            setAudioEnabled(true);
            setShowAudioConsent(false);
          }}
          onDeny={() => {
            setAudioEnabled(false);
            setShowAudioConsent(false);
          }}
        />
      )}

      <BrowserRouter>
        <AuthProvider>
          <MusicProvider>
            <GlobalAudio enabled={audioEnabled} />

            {!isComikReleafDomain && <Nav />}
            <MemberAccountMenu />

            <Routes>

          <Route
            path="/"
            element={
              isComikReleafDomain ? (
                <ComikReleaf setCart={setCart} />
              ) : (
                <Landing />
              )
            }
          />

          <Route
            path="/members"
            element={<Members />}
          />

          <Route
            path="/dashboard"
            element={<Dashboard />}
          />

          <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            <Route
              path="/stream-presets"
            element={<StreamPresets />}
          />
          <Route
            path="/coming-soon"
            element={<ComingSoon />}
          />

          <Route
            path="/song-catalogue"
            element={<SongCatalogue />}
          />

          <Route
              path="/comik-releaf"
              element={
                  <ComikReleaf
                      setCart={setCart}
                  />
              }
          />

          <Route
            path="/cart"
            element={
              cartTransferStatus === "loading" ? (
                <div className="p-8 text-center">
                  Restoring your cart...
                </div>
              ) : cartTransferStatus === "error" ? (
                <div className="p-8 text-center">
                  <p>{cartTransferError}</p>
                  <button
                    type="button"
                    onClick={() => {
                      window.location.href =
                        "https://doriantelive.com/cart";
                    }}
                  >
                    Return to Cart
                  </button>
                </div>
              ) : (
                <CheckoutPage
                  cart={cart}
                  onRemove={removeFromCart}
                  onQuantityChange={updateCartQuantity}
                />
              )
            }
          />

          <Route
              path="/checkout-test"
              element={
                  <CheckoutPage
                      cart={[
                          {
                              id: "checkout-test-item",
                              productId: "test-product",
                              variantId: "test-variant",
                              sku: "TEST-SKU-001",
                              title: "Checkout Test Shirt",
                              image: null,
                              variant: "Black / M",
                              price: 19.99,
                              quantity: 1,
                          },
                      ]}
                      onRemove={() => {}}
                      onQuantityChange={() => {}}
                  />
              }
          />

          <Route
            path="/discord-soon"
            element={
              <ComingSoon
                eyebrow="Discord // Open World OG's"
                body="Still learning more about security bots in order to ensure everyone's safety before making public."
              />
            }
          />

            </Routes>
          </MusicProvider>
        </AuthProvider>
      </BrowserRouter>

    </div>
  );
}

export default App;
